import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { Prospect, ProspectConversation } from 'model/ProspectMessage';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { reduceResolution, wbp } from 'Theme';
import Loader from '../../ui/Loader';
import ConversationItem from './ConversationItem';
import { fetchTwoWaySmsCountThunk } from 'actions/twoWaySms/twoWaySmsActions';
import { fetchProspectConversations } from 'actions/prospectMessage/prospectMessageActions';
import { MessageFilterTypes } from '../../Model';
import { ProspectMessageStatus } from 'reducers/prospectMessage/prospectMessageState';
import useNonInitialEffect from '@versiondos/hooks';

interface ConversationsProps {
    selectedProspect: Prospect | undefined;
    searchText: string;
    filter: string;
    setProspect: (prospect?: Prospect) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        },
        chatScroll: {
            width: '100%',
            height: '100%',
            overflowY: 'scroll',
            overflowX: 'hidden',
            padding: `${reduceResolution(15)}px ${reduceResolution(15)}px ${reduceResolution(15)}px ${reduceResolution(
                15
            )}px`,
            maxHeight: '770px',
            minHeight: '410px'
        },
        noMorePages: {
            color: '#7A7A7A',
            margin: '37px 0',
            textAlign: 'center'
        }
    })
);

export const Conversations: FunctionComponent<ConversationsProps> = props => {
    const size = 30;
    const classes = useStyles();
    const dispatch = useDispatch();
    const [scrollAvailable, setScrollAvailable] = useState(false);
    const chatScrollRef = useRef<OverlayScrollbarsComponent>(null);
    const lastVisibleChatRefId = useRef<string | null>(null);
    const prospectConversations = useSelector<RootState, Array<ProspectConversation>>(
        state => state.prospectMessages.prospectConversations
    );
    const prospectConversationStatus = useSelector<RootState, ProspectMessageStatus>(
        state => state.prospectMessages.status
    );
    const meta = useSelector((state: RootState) => state.prospectMessages.metaProspectConversations);

    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);

    const hasMorePages = useMemo(() => {
        return meta ? meta.page < meta.totalPages : false;
    }, [meta]);

    const fetchConversations = (page: number, pageSize: number) => {
        setLoading(true);
        dispatch(fetchTwoWaySmsCountThunk());
        dispatch(fetchProspectConversations(page, pageSize, props.filter as MessageFilterTypes, props.searchText));
    };

    useEffect(() => {
        fetchConversations(1, size);
    }, []);

    useEffect(() => {
        fetchConversations(page, size);
    }, [page]);

    useNonInitialEffect(() => {
        if (prospectConversationStatus === ProspectMessageStatus.FetchingConversationsSuccess) {
            setLoading(false);

            if (page > 1) {
                setTimeout(() => {
                    if (lastVisibleChatRefId.current) {
                        const el = document.getElementById(lastVisibleChatRefId.current);
                        el?.scrollIntoView({ block: 'start', behavior: 'auto' });
                    }
                }, 100);
            }
        }
    }, [prospectConversationStatus]);

    useNonInitialEffect(() => {
        setLoading(true);
        props.setProspect && props.setProspect();
        fetchConversations(1, size);
    }, [props.searchText, props.filter]);

    const handleChatScroll = () => {
        if (chatScrollRef.current) {
            const vp = chatScrollRef.current.osInstance()?.getElements().viewport;
            if (vp) {
                const { scrollTop, scrollHeight, clientHeight } = vp;
                setScrollAvailable(scrollHeight > clientHeight);
                const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

                const chatElements = vp.querySelectorAll('[id^="chat-"]');

                for (const element of Array.from(chatElements)) {
                    const rect = (element as HTMLElement).getBoundingClientRect();
                    const vpRect = vp.getBoundingClientRect();

                    if (rect.top >= vpRect.top && rect.bottom <= vpRect.bottom) {
                        lastVisibleChatRefId.current = element.id;
                    }
                }

                if (distanceFromBottom < 5 && hasMorePages && !loading) {
                    setPage(prev => prev + 1);
                }
            }
        }
    };

    return (
        <OverlayScrollbarsComponent
            className={classes.chatScroll}
            options={{
                callbacks: {
                    onScrollStop: handleChatScroll
                }
            }}
            ref={chatScrollRef}
        >
            {prospectConversations && (
                <Box className={classes.root}>
                    {loading && <Loader />}
                    {!loading &&
                        prospectConversations.map(prospectConversation => {
                            return (
                                <Box
                                    key={
                                        prospectConversation.lastMessage.uuid +
                                        '-' +
                                        prospectConversation.prospect.id +
                                        '-' +
                                        '-root'
                                    }
                                >
                                    <ConversationItem
                                        prospectConversation={prospectConversation}
                                        selected={prospectConversation.prospect.id === props.selectedProspect?.id}
                                        onClick={() => {
                                            props.setProspect(prospectConversation.prospect);
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    {meta &&
                        meta.page === meta.totalPages &&
                        prospectConversations.length > 0 &&
                        !(props.searchText.length > 3) &&
                        scrollAvailable && <Box className={classes.noMorePages}>{"🎉 You're all caught up."}</Box>}
                </Box>
            )}
        </OverlayScrollbarsComponent>
    );
};

export default Conversations;
